import articleConfig from '~/config/article';
import type { IArticleConfig } from '~/config/article';
import ServerLogger from './ServerLogger';
import WidgetsCreator from './WidgetsCreator';
import AdvertisementCreator from '~/modules/Advertisement';
import { debugLog } from '~/debug';

/** Params object that should be passed to the `window.newVuukleWidgets` */
interface INewWidgetsParams {
  /**
   * Index that should be added to selectors to find elements to create another set of widgets.
   * elementsIndex = 2: #vuukle-comments -> #vuukle-comments-2, #vuukle-emote -> #vuukle-emote-2
   * elementsIndex = 'b12': #vuukle-comments -> #vuukle-comments-b12; ...
   */
  elementsIndex: number | string;
  // ⬇ other configuration items that are similar to article config parameters.
  articleId: string;
  author?: string;
  img?: string;
  tags?: string | string[];
  title?: string;
  url?: string;
  commentingClosed?: boolean;
}

class EndlessMode {
  private stashedNewWidgetsCalls: Map<string, () => void> = new Map();
  public articleIDs: Map<string, string> = new Map();
  private firstTime = true;

  public initializeEndlessMode() {
    /** Watch for href changes before calling stashed newVuukleWidget function calls in endless mode when not in an SPA */
    if (!articleConfig.singlePage) {
      this.activateUrlChangeListener();
    }
  }

  constructor() {
    // In some cases platform.js might get inserted more than once
    // Which is why we're preventing re-assignment of newVuukleWidgets by duplicated copies of platform.js
    if (!window.VUUKLE_PLATFORM) {
      window.newVuukleWidgets = this.generateNewVuukleWidgets.bind(this);
      window.newVuukleDefaultWidgets = this.generateWidgetsAtDefaultSelectors.bind(this);
    }
  }

  private activateUrlChangeListener() {
    let oldHref = document.location.href.replace(document.location.search, '');

    setInterval(() => {
      if (oldHref !== document.location.href.replace(document.location.search, '')) {
        const newUrl = document.location.href.replace(document.location.search, '');
        oldHref = newUrl;
        // Call newVuukleWidgets call for current url and then remove the stashed callback
        const callback = this.stashedNewWidgetsCalls.get(newUrl);
        if (callback) {
          callback();
          this.stashedNewWidgetsCalls.delete(newUrl);
        }
      }
    }, 1000);
  }

  /**
   * Create method for site owner to create widgets using `window.newVuukleWidgets` based on config
   * and custom configuration.
   * This is mainly used on articles with endless scrolling where it's needed to create widgets on the same page
   * under a few articles. Publisher is required to pass custom params like new id, new title, new author etc.
   * Based on these values we update config and create widgets.
   * Also, publisher is required to pass `elementsIndex` inside object, using it we update selectors to select new wrappers
   * for widgets.
   * @see '~/modules/WidgetsCreator.ts' to understand how we select elements
   * @param {object} params - partial custom config to create articles with updated url, title etc. + includes index number that we use to select divs
   * to create widgets
   * @returns {void}
   */
  private async generateNewVuukleWidgets(params: INewWidgetsParams): Promise<void> {
    /**
     * Import the scheduler polyfill if not supported
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Prioritized_Task_Scheduling_API
     */
    if (!('scheduler' in window)) {
      await import(/* webpackChunkName: "sch", webpackPreload: true */ 'scheduler-polyfill');
    }
    // 🔧 Validate Params
    if (!params.articleId) {
      if (params.url) {
        params.articleId = params.url;
      } else {
        throw new Error('Article ID is required to create new Vuukle Widgets');
      }
    }
    if (!params.img) {
      console.warn(`[Vuukle] Article with the id ${params.articleId} has missing img.`);
      params.img = '';
    }
    if (!params.title) {
      console.warn(`[Vuukle] Article with the id ${params.articleId} has missing title.`);
      params.title = '';
    }

    /** Convert an array of strings into a comma separated string */
    if (params.tags && Array.isArray(params.tags)) {
      params.tags = params.tags.join(',');
    }

    /** In case the tags are not a string, provide them as blank */
    if (params.tags && typeof params.tags !== 'string') {
      params.tags = '';
    }

    if (!articleConfig.apiKey) {
      throw new Error('⚠ [VUUKLE] API key is missing in the VUUKLE_CONFIG');
    }

    // TODO: mb make customConfig to be of type of article config and just do deepMerge?? [Note: needs replacing customConfig.commentingClosed to customConfig.comments
    const updatedConfig: IArticleConfig = {
      ...articleConfig,
      articleId: params.articleId,
      author: params.author,
      img: params.img || '',
      tags: params.tags || '',
      title: params.title || '',
      url: params.url || '',
    };

    // Reset view_page fired every time new widgets are created in endless mode
    ServerLogger.viewPageFired = false;

    const callOrStashNewWidgetCallback = () => {
      // Callback to use for a later call
      const newWidgetCallback = async () => {
        try {
          const widgetsCreator = await WidgetsCreator.createWidgetsCreatorInstance(
            '-' + String(params.elementsIndex),
            updatedConfig
          );
          new AdvertisementCreator(
            '-' + String(params.elementsIndex),
            updatedConfig,
            widgetsCreator.communicationCreator,
            widgetsCreator
          );
        } catch (e) {
          debugLog('error', e.message);
        }
        // if (canInsertIVTReporter()) {
        //   ivtReporter = new IVTReporter(updatedConfig);
        // }
        // Check for IVT before proceeding
        // if (advancedKeys[articleConfig.apiKey] && advancedKeys[articleConfig.apiKey].blockIVT) {
        //   if (ivtReporter) {
        //     /** Listen for IVT code response, then generate ads */
        //     ivtReporter.addIVTListener((trafficType: number | undefined) => {
        //       if (!advancedKeys[articleConfig.apiKey].blockIVT.includes(trafficType)) {
        //         new AdvertisementCreator(
        //           '-' + String(params.elementsIndex),
        //           updatedConfig,
        //           widgetsCreator.communicationCreator,
        //           realtimeObj
        //         );
        //       }
        //     });
        //   }
        // } else if (!advancedKeys[articleConfig.apiKey]?.blockIVT) {
        //   new AdvertisementCreator(
        //     '-' + String(params.elementsIndex),
        //     updatedConfig,
        //     widgetsCreator.communicationCreator,
        //     realtimeObj
        //   );
        // }
        scheduler.postTask(
          () => {
            if (!articleConfig.noViewPage) {
              ServerLogger.log('view_page', updatedConfig);
            }
          },
          { priority: 'background' }
        );
      };

      // In SPAs - generate the widgets and advertisements immediately
      if (articleConfig.singlePage) {
        scheduler.postTask(() => {
          newWidgetCallback();
        });
      } else {
        // Otherwise stash callbacks for later use when url changes
        if (!this.firstTime) {
          // Stash the newVuukleWidgets call for the given url to call it later
          this.stashedNewWidgetsCalls.set(String(params.url), () => {
            scheduler.postTask(() => {
              newWidgetCallback();
            });
          });
        } else {
          // Execute ad and widget initialization for the first article right away
          scheduler.postTask(() => {
            newWidgetCallback();
          });
          this.firstTime = false;
        }
      }
      this.articleIDs.set(String(params.url), updatedConfig.articleId || '');
    };

    if (document.readyState !== 'loading') {
      scheduler.postTask(() => {
        callOrStashNewWidgetCallback();
      });
    } else {
      document.addEventListener('readystatechange', () => {
        if (document.readyState === 'interactive' || document.readyState === 'complete') {
          scheduler.postTask(() => {
            callOrStashNewWidgetCallback();
          });
        }
      });
    }
  }

  /**
   * Function that generates widgets at their default selectors
   *
   * @param params Article information
   */
  private async generateWidgetsAtDefaultSelectors(params: Omit<INewWidgetsParams, 'elementsIndex'>): Promise<void> {
    /**
     * Import the scheduler polyfill if not supported
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Prioritized_Task_Scheduling_API
     */
    if (!('scheduler' in window)) {
      await import(/* webpackChunkName: "sch", webpackPreload: true */ 'scheduler-polyfill');
    }
    // 🔧 Validate Params
    if (!params.articleId) {
      if (params.url) {
        params.articleId = params.url;
      } else {
        throw new Error('Article ID is required to create new Vuukle Widgets');
      }
    }
    if (!params.img) {
      console.warn(`[Vuukle] Article with the id ${params.articleId} has missing img.`);
      params.img = '';
    }
    if (!params.title) {
      console.warn(`[Vuukle] Article with the id ${params.articleId} has missing title.`);
      params.title = '';
    }

    /** Convert an array of strings into a comma separated string */
    if (params.tags && Array.isArray(params.tags)) {
      params.tags = params.tags.join(',');
    }

    /** In case the tags are not a string, provide them as blank */
    if (params.tags && typeof params.tags !== 'string') {
      params.tags = '';
    }

    if (!articleConfig.apiKey) {
      throw new Error('⚠ [VUUKLE] API key is missing in the VUUKLE_CONFIG');
    }

    const updatedConfig: IArticleConfig = {
      ...articleConfig,
      articleId: params.articleId,
      author: params.author,
      img: params.img || '',
      tags: params.tags || '',
      title: params.title || '',
      url: params.url || '',
    };

    // Reset view_page fired every time new widgets are created
    ServerLogger.viewPageFired = false;

    try {
      const widgetsCreator = await WidgetsCreator.createWidgetsCreatorInstance('', updatedConfig);
      new AdvertisementCreator('', updatedConfig, widgetsCreator.communicationCreator, widgetsCreator);
    } catch (e) {
      debugLog('error', e.message);
    }

    scheduler.postTask(
      () => {
        if (!articleConfig.noViewPage) {
          ServerLogger.log('view_page', updatedConfig);
        }
      },
      { priority: 'background' }
    );
    this.articleIDs.set(String(params.url), updatedConfig.articleId || '');
  }
}

export default new EndlessMode();
